import { SELECT_OPTIONCLASSIFICATION } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { fetchOptions } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { RECEIVE_OPTIONS } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { selectMultipleOptions } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { OptionClassificationIdentifiers, SurfaceOptionIdentifiers } from '../../Enums';
import { DEFAULT_CANVAS_HEIGHT_MM, DEFAULT_CANVAS_WIDTH_MM } from '../../utils/editorUnits';
import { itemPatchNeedsLayout, resolveItemLayout } from '../../utils/editorItemLayout';
import { buildNonSvgImageUploadPatch, type ImageUploadPreview } from '../../utils/imageItemLayout';
import { fetchSvgSource, isSvgUrl } from '../../utils/svgDocument';
import {
  buildHexToColorOptionId,
  computeMeasurementsByColor,
  enrichEditorItemMetrics,
  getColorMappingOptionsFromConfiguratorState,
  getColorOptionsFromConfiguratorState,
} from '../../utils/itemMetrics';
import type { FontRecord } from '../../utils/fonts';
import type { ItemSizeLimits } from '../../utils/itemSizing';
import { buildDesignDataFromEditor, type DesignData } from '../../utils/buildDesignData';
import { syncDesignColorOptions } from '../../utils/designColorSync';
import { getSelectedColorCount } from '../../utils/optionClassifications';
import type { Bounds, EditorItem, EditorState, MeasurementsByColor, TextItem } from './types';
import { createInitialEditorState } from './types';
import { getDefaultCanvasSize } from './Selectors';

export const actionTypes = {
  SET_EDITOR_STATE: 'XXLPRINT/SET_EDITOR_STATE',
  SET_EDITOR_SELECTED_ID: 'XXLPRINT/SET_EDITOR_SELECTED_ID',
  SET_EDITOR_ADD_MODE: 'XXLPRINT/SET_EDITOR_ADD_MODE',
  ADD_EDITOR_ITEM: 'XXLPRINT/ADD_EDITOR_ITEM',
  UPDATE_EDITOR_ITEM: 'XXLPRINT/UPDATE_EDITOR_ITEM',
  REMOVE_EDITOR_ITEM: 'XXLPRINT/REMOVE_EDITOR_ITEM',
  SET_EDITOR_ITEM_COLOR: 'XXLPRINT/SET_EDITOR_ITEM_COLOR',
  SET_EDITOR_IMAGE_COLOR_AREA: 'XXLPRINT/SET_EDITOR_IMAGE_COLOR_AREA',
  RESET_EDITOR_IMAGE_COLOR_AREAS: 'XXLPRINT/RESET_EDITOR_IMAGE_COLOR_AREAS',
  SET_EDITOR_ITEM_OPTION: 'XXLPRINT/SET_EDITOR_ITEM_OPTION',
  SET_EDITOR_DESIGN_SIZE: 'XXLPRINT/SET_EDITOR_DESIGN_SIZE',
  SET_BLOCKED_MATERIAL_ID: 'XXLPRINT/SET_BLOCKED_MATERIAL_ID',
} as const;

// The material option the user tried to select but that is blocked by the current
// color selection. Drives the material section note and the per-item conflict badges.
export const setBlockedMaterialId = (materialId: string | null) => ({
  type: actionTypes.SET_BLOCKED_MATERIAL_ID,
  materialId,
});

export const setEditorSelectedId = (selectedId: string | null) => ({
  type: actionTypes.SET_EDITOR_SELECTED_ID,
  selectedId,
});

export const setEditorAddMode = (addMode: 'text' | 'image' | null) => ({
  type: actionTypes.SET_EDITOR_ADD_MODE,
  addMode,
});

export const addEditorItemSync = (item: EditorItem) => ({
  type: actionTypes.ADD_EDITOR_ITEM,
  item,
});

export const updateEditorItemSync = (id: string, patch: Partial<EditorItem>) => ({
  type: actionTypes.UPDATE_EDITOR_ITEM,
  id,
  patch,
});

export const removeEditorItem = (id: string) => ({
  type: actionTypes.REMOVE_EDITOR_ITEM,
  id,
});

export const removeEditorItemAndSync = (id: string) => (dispatch, getState) => {
  dispatch(removeEditorItem(id));

  syncDesignColorOptions(dispatch, getState);

  const state = getState();
  const editor: EditorState =
    state.configurator?.configuration?.customdata?.editor || createInitialEditorState();
  const items = Object.values(editor.objects) as EditorItem[];
  const optionsList = state.configurator?.optionsList || {};
  const surfaceOptions: unknown[] = optionsList[OptionClassificationIdentifiers.surface] || [];
  const usedSurfaceIds = new Set(
    items.flatMap((item) => {
      const optionId = item.selectedOptions?.[OptionClassificationIdentifiers.surface] ?? null;
      return optionId ? [optionId] : [];
    })
  );

  dispatch(
    selectMultipleOptions([
      {
        identifier: OptionClassificationIdentifiers.surface,
        selectedoptions: surfaceOptions.map((opt) => ({
          identifier: (opt as { identifier: string }).identifier,
          amount: usedSurfaceIds.has((opt as { identifier: string }).identifier) ? 1 : 0,
        })),
      },
    ])
  );
};

export const setEditorItemColor = (id: string, color: string, colorOptionId: string) => ({
  type: actionTypes.SET_EDITOR_ITEM_COLOR,
  id,
  color,
  colorOptionId,
});

export const setEditorItemColorAndSync =
  (id: string, color: string, colorOptionId: string) => (dispatch, getState) => {
    dispatch(setEditorItemColor(id, color, colorOptionId));
    syncDesignColorOptions(dispatch, getState);
  };

export const setEditorImageColorArea = (
  id: string,
  areaIndex: number,
  color: string,
  colorOptionId: string
) => ({
  type: actionTypes.SET_EDITOR_IMAGE_COLOR_AREA,
  id,
  areaIndex,
  color,
  colorOptionId,
});

export const setEditorImageColorAreaAndSync =
  (id: string, areaIndex: number, color: string, colorOptionId: string) =>
  (dispatch, getState) => {
    dispatch(setEditorImageColorArea(id, areaIndex, color, colorOptionId));
    syncDesignColorOptions(dispatch, getState);
  };

export const resetEditorImageColorAreas = (id: string) => ({
  type: actionTypes.RESET_EDITOR_IMAGE_COLOR_AREAS,
  id,
});

export const resetEditorImageColorAreasAndSync =
  (id: string) => (dispatch, getState) => {
    dispatch(resetEditorImageColorAreas(id));
    syncDesignColorOptions(dispatch, getState);
  };

export const setEditorItemOption = (id: string, classificationId: string, optionIdentifier: string) => ({
  type: actionTypes.SET_EDITOR_ITEM_OPTION,
  id,
  classificationId,
  optionIdentifier,
});

export const switchAllEditorItemsFromMattToGlossAndSync = () => (dispatch, getState) => {
  const state = getState();
  const editor: EditorState =
    state.configurator?.configuration?.customdata?.editor || createInitialEditorState();
  const items = Object.values(editor.objects) as EditorItem[];

  let changed = false;
  items.forEach((item) => {
    if (item.selectedOptions?.[OptionClassificationIdentifiers.surface] === SurfaceOptionIdentifiers.matte) {
      dispatch(
        setEditorItemOption(item.id, OptionClassificationIdentifiers.surface, SurfaceOptionIdentifiers.gloss)
      );
      changed = true;
    }
  });

  if (!changed) return;

  const updatedEditor: EditorState =
    getState().configurator?.configuration?.customdata?.editor || createInitialEditorState();
  const updatedItems = Object.values(updatedEditor.objects) as EditorItem[];
  const optionsList = getState().configurator?.optionsList || {};
  const surfaceOptions: unknown[] = optionsList[OptionClassificationIdentifiers.surface] || [];
  const usedSurfaceIds = new Set(
    updatedItems.flatMap((item) => {
      const optionId = item.selectedOptions?.[OptionClassificationIdentifiers.surface] ?? null;
      return optionId ? [optionId] : [];
    })
  );

  dispatch(
    selectMultipleOptions([
      {
        identifier: OptionClassificationIdentifiers.surface,
        selectedoptions: surfaceOptions.map((opt) => ({
          identifier: (opt as { identifier: string }).identifier,
          amount: usedSurfaceIds.has((opt as { identifier: string }).identifier) ? 1 : 0,
        })),
      },
    ])
  );
};

function getEditorObjects(state: {
  configuration?: { customdata?: { editor?: EditorState } };
}): Record<string, EditorItem> {
  return state.configuration?.customdata?.editor?.objects || {};
}

function getFonts(getState: () => Record<string, unknown>): FontRecord[] {
  return (getState().fonts || []) as FontRecord[];
}

function getCanvasSizeLimits(getState: () => Record<string, unknown>): ItemSizeLimits {
  const size = getDefaultCanvasSize(getState());
  return {
    maxHeight: size.height || DEFAULT_CANVAS_HEIGHT_MM,
    maxWidth: size.width || DEFAULT_CANVAS_WIDTH_MM,
  };
}

export const addEditorItem =
  (item: EditorItem) =>
  async (dispatch: (action: unknown) => void, getState: () => Record<string, unknown>) => {
    const layoutFields = await resolveItemLayout(
      item,
      getFonts(getState),
      getCanvasSizeLimits(getState),
      getColorMappingOptionsFromConfiguratorState(
        getState().configurator as { optionsList?: Record<string, unknown[]>; configuration?: { optionclassifications?: unknown } }
      )
    );
    dispatch(addEditorItemSync({ ...item, ...layoutFields } as EditorItem));
  };

export const updateEditorImageFromUpload =
  (id: string, url: string, options: { preview?: ImageUploadPreview; imageData?: Record<string, unknown> } = {}) =>
  async (dispatch: (action: unknown) => void, getState: () => Record<string, unknown>) => {
    if (isSvgUrl(url)) {
      const rawSvg = await fetchSvgSource(url);
      await dispatch(
        updateEditorItem(id, {
          url,
          ...(options.imageData ? { imageData: options.imageData } : {}),
          ...(rawSvg ? { svgSource: rawSvg, colorAreas: [], colorAreaBounds: [] } : {}),
        })
      );
      return;
    }

    await dispatch(updateEditorItem(id, buildNonSvgImageUploadPatch(url, options.preview)));
  };

export const updateEditorItem =
  (id: string, patch: Partial<EditorItem>) =>
  async (dispatch: (action: unknown) => void, getState: () => Record<string, unknown>) => {
    const state = getState().configurator as Parameters<typeof getEditorObjects>[0];
    const existing = getEditorObjects(state)[id];
    if (!existing) return;

    if (itemPatchNeedsLayout(existing, patch)) {
      if (existing.type === 'text') {
        const textPatch = patch as Partial<TextItem>;
        const immediatePatch: Partial<TextItem> = {};
        if (textPatch.content !== undefined) immediatePatch.content = textPatch.content;
        if (textPatch.fontFamily !== undefined) immediatePatch.fontFamily = textPatch.fontFamily;
        if (Object.keys(immediatePatch).length > 0) {
          dispatch(updateEditorItemSync(id, immediatePatch));
        }
      }

      const merged = { ...existing, ...patch } as EditorItem;
      const layoutPatch = await resolveItemLayout(
        merged,
        getFonts(getState),
        getCanvasSizeLimits(getState),
        getColorMappingOptionsFromConfiguratorState(
          getState().configurator as { optionsList?: Record<string, unknown[]>; configuration?: { optionclassifications?: unknown } }
        )
      );
      dispatch(updateEditorItemSync(id, { ...patch, ...layoutPatch }));
      if (
        merged.type === 'image' &&
        'colorAreas' in layoutPatch &&
        Array.isArray(layoutPatch.colorAreas) &&
        layoutPatch.colorAreas.length > 0
      ) {
        syncDesignColorOptions(dispatch, getState);
      }
      return;
    }

    dispatch(updateEditorItemSync(id, patch));
  };

export const selectOptionClassificationAndFetchOptions =
  (optionclassification: { identifier: string }, triggerFetchOptions = false) =>
  async (dispatch, getState) => {
    dispatch({
      type: SELECT_OPTIONCLASSIFICATION,
      optionclassification,
    });

    if (triggerFetchOptions) {
      const { configuration } = getState().configurator;
      await dispatch(
        fetchOptions(configuration.item.identifier, optionclassification.identifier, configuration)
      );
    }
  };

export { RECEIVE_OPTIONS };

function getEditorState(state: { configuration?: { customdata?: { editor?: EditorState } } }): EditorState {
  return state.configuration?.customdata?.editor || createInitialEditorState();
}

function withEditor(
  state: { configuration: Record<string, unknown>; optionsList?: Record<string, unknown[]> },
  editor: EditorState
) {
  const hexToOptionId = buildHexToColorOptionId(getColorOptionsFromConfiguratorState(state));
  const objects = Object.fromEntries(
    Object.entries(editor.objects).map(([id, item]) => [id, enrichEditorItemMetrics(item, hexToOptionId)])
  );
  const enrichedEditor = { ...editor, objects };
  const measurementsByColor = computeMeasurementsByColor(objects, hexToOptionId);

  const canvasSize = getDefaultCanvasSize(state as Parameters<typeof getDefaultCanvasSize>[0]);
  const existingDesignData = (state.configuration.designdata as DesignData) || {};
  const existingArea = existingDesignData.default || {};
  const designdata = buildDesignDataFromEditor(
    enrichedEditor,
    {
      canvasWidthMm: canvasSize.width || DEFAULT_CANVAS_WIDTH_MM,
      canvasHeightMm: canvasSize.height || DEFAULT_CANVAS_HEIGHT_MM,
      designProductionMethodIdentifier: existingArea.designProductionMethodIdentifier,
      colorAmount: getSelectedColorCount(state.configuration.optionclassifications),
    },
    existingDesignData
  );

  return {
    ...state,
    configuration: {
      ...state.configuration,
      designdata,
      customdata: {
        ...(state.configuration.customdata as Record<string, unknown>),
        editor: enrichedEditor,
        measurementsByColor,
      },
    },
  };
}

export function applyEditorUpdate(
  state: { configuration: Record<string, unknown>; optionsList?: Record<string, unknown[]> },
  updater: (editor: EditorState) => EditorState
) {
  const editor = updater(getEditorState(state));
  return withEditor(state, editor);
}

export type { Bounds, EditorItem, EditorState, MeasurementsByColor };
